import { Form, Formik, FormikProps } from 'formik';
import { Ref, useEffect, useRef } from 'react';
import { AnyObject } from 'yup';
import OnboardFormLayout from '../../components/OnboardFormLayout';
import OptionalTextDivider from '../../components/OptionalTextDivider';
import { useDivideSchemaFields } from '../../hooks/useDivideSchemaFields';
import { BaseFormControls, SlotProps } from '../GenericForm';
import { useCRUDContext } from '../context/CRUDContext';
import { EntityIsNotSelected } from './components/EntityIsNotSelected';
import { FieldsArea } from './components/FieldsArea';
import { FormFieldType } from './components/FormFieldType';
import GenericFormField from './components/GenericFormField';
import { fixedForwardRef } from './utils/forwardRefFix';
import { resetFormUsingKey } from './utils/resetFormUsingKey';
import { useFormControllerRefSetup } from './utils/useFormControllerRefSetup';

function ViewFormInner<T extends AnyObject>(
  props: SlotProps<T>,
  ref?: Ref<BaseFormControls | null>
) {
  const crud = useCRUDContext();
  const formikRef = useRef<FormikProps<Partial<T>> | null>(null);
  useFormControllerRefSetup(ref, formikRef);

  // Reset form when entity changes
  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.resetForm({ values: crud.currEntity });
    }
  }, [crud.currEntity, formikRef]);

  const { requiredFields, optionalFields } = useDivideSchemaFields(
    crud.yupSchemaOut
  );

  const renderFormFields = (fieldNames: string[]) =>
    fieldNames
      .filter((key) => !props?.hiddenFields?.includes(key))
      .map((key) => {
        const fieldProps = props.fieldsConfig?.[key] || {
          type: FormFieldType.TextDisplay,
          label: key,
          initialValue: '',
        };
        return <GenericFormField key={key} name={key} fieldMeta={fieldProps} />;
      });

  if (!crud.currEntity) {
    return <EntityIsNotSelected />;
  }

  const formKey = resetFormUsingKey(crud.currEntity);

  return (
    <Formik
      key={formKey}
      innerRef={formikRef}
      initialValues={crud.currEntity}
      validationSchema={crud.yupSchemaOut}
      onSubmit={() => {
        console.log('edge case submitting form');
      }}
    >
      {() => (
        <Form
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <OnboardFormLayout
            onSubmit={() => {
              console.log('edge case submitting form');
            }}
          >
            {/* Here we can also do ordering by optional meta to set required fields first */}
            <FieldsArea {...props.gridConfig}>
              {requiredFields.length > 0 && renderFormFields(requiredFields)}
            </FieldsArea>
            {optionalFields.length > 0 && <OptionalTextDivider />}
            <FieldsArea {...props.gridConfig}>
              {optionalFields.length > 0 && renderFormFields(optionalFields)}
            </FieldsArea>
          </OnboardFormLayout>
        </Form>
      )}
    </Formik>
  );
}

export const ViewForm = fixedForwardRef(ViewFormInner);
